import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  HeartHandshake,
  User,
  Languages,
  MessageSquare,
  Check,
  BadgeCheck,
  CalendarDays,
  Sparkles,
  HelpCircle,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { useI18n, type DictKey } from "@/lib/i18n";
import { applyAsVolunteer } from "@/lib/volunteers-api";

export const Route = createFileRoute("/volunteer")({
  head: () => ({
    meta: [
      { title: "Become a Volunteer Guide — Jain Gaurav Yatra" },
      {
        name: "description",
        content:
          "Apply to guide fellow devotees across Ahmedabad's 31 Jain Derasars. Share your knowledge and be part of the Yatra Seva family.",
      },
    ],
  }),
  component: VolunteerPage,
});

const LANGUAGE_OPTIONS = ["Gujarati", "Hindi", "English", "Marwari", "Marathi", "Sanskrit"];

const WHY_ITEMS: { icon: typeof HeartHandshake; tk: DictKey; dk: DictKey }[] = [
  { icon: HeartHandshake, tk: "volp_why1_t", dk: "volp_why1_d" },
  { icon: BadgeCheck, tk: "volp_why2_t", dk: "volp_why2_d" },
  { icon: CalendarDays, tk: "volp_why3_t", dk: "volp_why3_d" },
  { icon: Sparkles, tk: "volp_why4_t", dk: "volp_why4_d" },
];

/** Mirrors the volunteers API exactly — no fields the backend can't store. */
type Form = {
  name: string;
  email: string;
  phone: string;
  languages: string[];
  experience_years: string;
  charges_per_day: string;
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
};

const EMPTY: Form = {
  name: "",
  email: "",
  phone: "",
  languages: [],
  experience_years: "0",
  charges_per_day: "500",
  bio_en: "",
  bio_hi: "",
  bio_gu: "",
};

function VolunteerPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const u = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));
  const toggleLanguage = (value: string) =>
    setForm((f) => ({
      ...f,
      languages: f.languages.includes(value)
        ? f.languages.filter((x) => x !== value)
        : [...f.languages, value],
    }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("vol_form_required"));
      return;
    }
    setSubmitting(true);
    try {
      await applyAsVolunteer({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        languages: form.languages,
        experience_years: form.experience_years ? Number(form.experience_years) : 0,
        charges_per_day: form.charges_per_day ? Number(form.charges_per_day) : 0,
        bio_en: form.bio_en.trim(),
        bio_hi: form.bio_hi.trim(),
        bio_gu: form.bio_gu.trim(),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "hsl(var(--cream-deep))" }}>
      <SiteHeader />

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">
            <HeartHandshake className="h-3 w-3" /> {t("vol_chip")}
          </span>
          <h1 className="jgy-section-title mt-4">
            {t("volp_title_pre")} <span className="text-primary">{t("volp_title_hl")}</span>{" "}
            {t("volp_title_post")}
          </h1>
          <p className="jgy-section-lead mt-4">{t("volp_desc")}</p>
        </div>

        {done ? (
          <div className="mx-auto mt-8 max-w-lg rounded-3xl border bg-card p-10 text-center shadow-[var(--shadow-soft)]">
            <span className="icon-tile mx-auto">
              <Check className="h-5 w-5" />
            </span>
            <h2 className="mt-4 font-serif text-xl font-bold">{t("vol_form_success")}</h2>
            <button onClick={() => navigate({ to: "/" })} className="btn-primary mx-auto mt-6">
              {t("back")}
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
            {/* Sidebar */}
            <aside className="space-y-4">
              <div className="rounded-2xl border bg-card p-5">
                <h2 className="font-serif text-base font-bold">{t("volp_why_title")}</h2>
                <ul className="mt-4 space-y-4">
                  {WHY_ITEMS.map((w) => (
                    <li key={w.tk} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <w.icon className="h-3.5 w-3.5" />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{t(w.tk)}</div>
                        <div className="text-xs text-muted-foreground">{t(w.dk)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border bg-muted/40 p-5">
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-primary" />
                  <h2 className="font-serif text-sm font-bold">{t("volp_help_title")}</h2>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">{t("volp_help_desc")}</p>
              </div>
            </aside>

            {/* Form */}
            <div className="rounded-3xl border bg-card p-6 shadow-[var(--shadow-soft)] sm:p-8">
              <SectionHead icon={User} title={t("volp_personal")} />
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t("volp_full_name")} required>
                  <input
                    value={form.name}
                    onChange={(e) => u({ name: e.target.value })}
                    placeholder="Ramesh Patel"
                    className="vinp"
                  />
                </Field>
                <Field label={t("volp_mobile")} required>
                  <input
                    value={form.phone}
                    onChange={(e) => u({ phone: e.target.value })}
                    placeholder="+91 98XXXXXXXX"
                    className="vinp"
                  />
                </Field>
                <Field label={t("volp_email")} full>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => u({ email: e.target.value })}
                    placeholder="you@example.com"
                    className="vinp"
                  />
                </Field>
                <Field label={t("volp_exp")}>
                  <input
                    type="number"
                    min={0}
                    value={form.experience_years}
                    onChange={(e) => u({ experience_years: e.target.value })}
                    className="vinp"
                  />
                </Field>
                <Field label={t("volp_charges")}>
                  <input
                    type="number"
                    min={0}
                    value={form.charges_per_day}
                    onChange={(e) => u({ charges_per_day: e.target.value })}
                    className="vinp"
                  />
                </Field>
              </div>

              <Divider />

              <SectionHead icon={Languages} title={t("volp_languages")} required />
              <div className="mt-3 flex flex-wrap gap-2">
                {LANGUAGE_OPTIONS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => toggleLanguage(l)}
                    className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                      form.languages.includes(l)
                        ? "border-primary bg-primary/15 text-primary"
                        : "border-border bg-background text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <Divider />

              <SectionHead icon={MessageSquare} title={t("volp_about")} />
              <div className="mt-3 space-y-3">
                <Field label={t("volp_short_bio")} full>
                  <textarea
                    rows={3}
                    value={form.bio_en}
                    onChange={(e) => u({ bio_en: e.target.value })}
                    placeholder={t("volp_short_bio_ph")}
                    className="vinp"
                  />
                </Field>
                <Field label={t("volp_bio_hi")} full>
                  <textarea
                    rows={3}
                    value={form.bio_hi}
                    onChange={(e) => u({ bio_hi: e.target.value })}
                    className="vinp"
                  />
                </Field>
                <Field label={t("volp_bio_gu")} full>
                  <textarea
                    rows={3}
                    value={form.bio_gu}
                    onChange={(e) => u({ bio_gu: e.target.value })}
                    className="vinp"
                  />
                </Field>
              </div>

              <button
                onClick={submit}
                disabled={submitting}
                className="btn-primary mt-6 w-full justify-center disabled:pointer-events-none disabled:opacity-60"
              >
                <HeartHandshake className="h-4 w-4" />
                {submitting ? t("vol_form_submitting") : t("volp_submit")}
              </button>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">{t("volp_fine")}</p>
            </div>
          </div>
        )}
      </div>

      <SiteFooter />
      <style>{`.vinp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}.vinp:focus{border-color:hsl(var(--primary))}`}</style>
    </div>
  );
}

function SectionHead({
  icon: Icon,
  title,
  required,
}: {
  icon: typeof HeartHandshake;
  title: string;
  required?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-primary" />
      <h2 className="font-serif text-base font-bold text-foreground">
        {title}
        {required ? <span className="text-destructive"> *</span> : null}
      </h2>
    </div>
  );
}

function Divider() {
  return <div className="my-6 border-t border-dashed border-border" />;
}

function Field({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
